import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import StatCard from '../components/admin/StatCard';
import AlertCard from '../components/admin/AlertCard';
import ApplicationCard from '../components/admin/ApplicationCard';
import MoverApplicationModal from '../components/admin/MoverApplicationModal';
import TruckerApplicationCard from '../components/admin/TruckerApplicationCard';
import UserManagement from '../components/admin/UserManagement';
import { MoverManagement } from '../components/admin/movers';
import { LeadsManagement } from '../components/admin/leads';
import TruckerRegistrationApplications from '../components/admin/TruckerRegistrationApplications';
import PendingDocuments from '../components/admin/PendingDocuments';
import InstantLeads from '../components/admin/InstantLeads';
import MoverReviews from '../components/admin/MoverReviews';
import adminApi from '../services/adminApi';
import RevenueLeadsChart from '../components/admin/charts/RevenueLeadsChart';
import LeadStatusPie from '../components/admin/charts/LeadStatusPie';
import supabase from '../lib/supabaseClient';

const ADMIN_TAB_STORAGE_KEY = 'adminActiveTab';
const ADMIN_TABS = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'movers', label: 'Movers', icon: '🚚' },
    { id: 'trucker-applications', label: 'Mover Applications', icon: '🚛' },
    { id: 'leads', label: 'Leads', icon: '📋' },
    { id: 'pending-documents', label: 'Pending Documents', icon: '📄' },
    { id: 'instant-leads', label: 'Instant Leads', icon: '⚡' },
    { id: 'reviews', label: 'Reviews', icon: '⭐' }
];
const ADMIN_TAB_IDS = new Set(ADMIN_TABS.map((tab) => tab.id));

const Admin = () => {
    const [activeTab, setActiveTab] = useState(() => {
        if (typeof window === 'undefined') return 'dashboard';

        const savedTab = localStorage.getItem(ADMIN_TAB_STORAGE_KEY);
        return ADMIN_TAB_IDS.has(savedTab) ? savedTab : 'dashboard';
    });
    const [stats, setStats] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [applications, setApplications] = useState([]);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [truckerApplications, setTruckerApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [leadStatusData, setLeadStatusData] = useState({
        pending: 0,
        completed: 0,
        assigned: 0,
        instant_pending: 0,
        instant_contacted: 0
    });

    useEffect(() => {
        loadDashboardData();

        // Auto-refresh dashboard every 5 minutes
        const intervalId = setInterval(() => {
            loadDashboardData();
        }, 5 * 60 * 1000); // 5 minutes

        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        localStorage.setItem(ADMIN_TAB_STORAGE_KEY, activeTab);
    }, [activeTab]);

    const loadDashboardData = async () => {
        try {
            setLoading(true);

            // Fetch real data from Supabase
            const [
                { count: activeMoversCount },
                { count: totalLeadsCount },
                { count: pendingInsuranceCount },
                { count: pendingLicensesCount },
                { data: recentApplicationsData },
                { data: leadsData },
                { data: instantLeadsData }
            ] = await Promise.all([
                // Active Movers count
                supabase
                    .from('movers_data')
                    .select('*', { count: 'exact', head: true })
                    .eq('is_active', true),

                // Total Leads count
                supabase
                    .from('leads')
                    .select('*', { count: 'exact', head: true }),

                // Pending Insurance count
                supabase
                    .from('mover_insurance')
                    .select('*', { count: 'exact', head: true })
                    .eq('status', 'pending'),

                // Pending Licenses count
                supabase
                    .from('mover_licenses')
                    .select('*', { count: 'exact', head: true })
                    .eq('status', 'pending'),

                // Recent Mover Applications (pending only)
                supabase
                    .from('movers_application')
                    .select('*')
                    .eq('status', 'Pending')
                    .order('submitted_at', { ascending: false })
                    .limit(3),

                // Fetch all leads for status distribution
                supabase
                    .from('leads')
                    .select('status'),

                // Fetch instant leads for status distribution
                supabase
                    .from('instant_leads')
                    .select('status')
            ]);

            // Calculate pending actions (insurance + licenses verification)
            const pendingActions = (pendingInsuranceCount || 0) + (pendingLicensesCount || 0);

            // Calculate lead status distribution
            const leadStatusCounts = {
                pending: 0,
                completed: 0,
                assigned: 0,
                instant_pending: 0,
                instant_contacted: 0
            };

            // Count regular leads by status
            (leadsData || []).forEach(lead => {
                const status = lead.status?.toLowerCase();
                if (status === 'pending') leadStatusCounts.pending++;
                else if (status === 'completed') leadStatusCounts.completed++;
                else if (status === 'assigned') leadStatusCounts.assigned++;
            });

            // Count instant leads by status
            (instantLeadsData || []).forEach(lead => {
                const status = lead.status?.toLowerCase();
                if (status === 'pending') leadStatusCounts.instant_pending++;
                else if (status === 'contacted') leadStatusCounts.instant_contacted++;
            });

            // Store lead status data for pie chart
            setLeadStatusData(leadStatusCounts);

            // Construct stats object
            const statsData = {
                totalRevenue: 0, // Keep mock for now - can be calculated from leads
                revenueTrend: { value: '0%', isPositive: true, period: 'from last month' },
                activeMovers: activeMoversCount || 0,
                moversTrend: null, // Removed trend display
                totalLeads: totalLeadsCount || 0,
                leadsPeriod: 'This month',
                pendingActions: pendingActions,
                actionsNote: pendingActions > 0 ? `${pendingInsuranceCount || 0} insurance, ${pendingLicensesCount || 0} licenses` : 'All clear'
            };

            // Transform recent applications to match expected format
            const transformedApplications = (recentApplicationsData || []).map(app => ({
                id: app.id,
                companyName: app.company_name,
                type: 'Professional', // Can be derived from subscription plan if available
                services: app.years_in_business ? `${app.years_in_business} yrs exp` : 'New',
                time: getTimeAgo(app.submitted_at),
                verified: app.status !== 'Rejected',
                status: app.status
            }));

            const alertsData = await adminApi.getCriticalAlerts();

            setStats(statsData);
            setAlerts(alertsData);
            setTruckerApplications(transformedApplications);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    // Helper function to calculate time ago
    const getTimeAgo = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    };

    // Load mover applications from Supabase
    useEffect(() => {
        let mounted = true;

        const fetchApplicationsFromSupabase = async () => {
            try {
                const { data, error } = await supabase
                    .from('movers_application')
                    .select('*')
                    .order('submitted_at', { ascending: false });

                if (error) throw error;

                if (mounted) {
                    setApplications(data || []);
                }
            } catch (error) {
            }
        };

        fetchApplicationsFromSupabase();

        return () => { mounted = false; };
    }, []);

    const handleApprove = async (application) => {
        try {
            // Update application status in Supabase
            const { error: updateError } = await supabase
                .from('movers_application')
                .update({
                    status: 'Approved',
                    reviewed_at: new Date().toISOString()
                })
                .eq('id', application.id);

            if (updateError) throw updateError;

            // Insert into movers_data table
            const { error: insertError } = await supabase
                .from('movers_data')
                .insert({
                    email: application.email,
                    phone: application.phone,
                    company_name: application.company_name,
                    website_link: application.website_link,
                    business_address: application.business_address,
                    city: application.city,
                    state: application.state,
                    zip_code: application.zip_code,
                    years_in_business: application.years_in_business,
                    google_profile_link: application.google_profile_link,
                    number_of_employees: application.number_of_employees,
                    company_description: application.company_description,
                    is_active: true,
                    approved_at: new Date().toISOString()
                });

            if (insertError) throw insertError;

            // Refresh applications
            const { data: refreshedData } = await supabase
                .from('movers_application')
                .select('*')
                .order('submitted_at', { ascending: false });

            setApplications(refreshedData || []);
            setSelectedApplication(null);

            // Reload dashboard stats
            await loadDashboardData();
        } catch (error) {
            toast.error('Failed to approve application: ' + error.message);
        }
    };

    const handleReject = async (application) => {
        try {
            // Update application status in Supabase
            const { error } = await supabase
                .from('movers_application')
                .update({
                    status: 'Rejected',
                    reviewed_at: new Date().toISOString(),
                    rejection_reason: 'Rejected by admin'
                })
                .eq('id', application.id);

            if (error) throw error;

            // Refresh applications
            const { data: refreshedData } = await supabase
                .from('movers_application')
                .select('*')
                .order('submitted_at', { ascending: false });

            setApplications(refreshedData || []);
            setSelectedApplication(null);

            // Reload dashboard stats
            await loadDashboardData();
        } catch (error) {
            toast.error('Failed to reject application: ' + error.message);
        }
    };

    const handleReviewAlert = async (alert) => {
        try {
            await adminApi.reviewAlert(alert.id);
            // Reload alerts
            const alertsData = await adminApi.getCriticalAlerts();
            setAlerts(alertsData);
        } catch (error) {
        }
    };

    const handleTruckerApprove = async (application) => {
        try {
            // Frontend handling - update status locally
            setTruckerApplications(prev =>
                prev.map(app =>
                    app.id === application.id
                        ? { ...app, status: 'approved' }
                        : app
                )
            );

            // TODO: Add backend API call when ready
        } catch (error) {
        }
    };

    const handleTruckerReject = async (application) => {
        try {
            // Frontend handling - update status locally
            setTruckerApplications(prev =>
                prev.map(app =>
                    app.id === application.id
                        ? { ...app, status: 'rejected' }
                        : app
                )
            );

            // TODO: Add backend API call when ready
        } catch (error) {
        }
    };

    const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const leadsSeries = [0, 0, 0, 0, 0, 0];
    const revenueSeries = [0, 0, 0, 0, 0, 0];

    // Prepare pie chart data from real lead statuses
    const pieLabels = [
        'Pending',
        'Completed',
        'Assigned',
        'Instant Pending',
        'Instant Contacted'
    ];
    const pieValues = [
        leadStatusData.pending,
        leadStatusData.completed,
        leadStatusData.assigned,
        leadStatusData.instant_pending,
        leadStatusData.instant_contacted
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-yellow-50/40 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-yellow-50/40">
            {/* Header */}
            <div className="bg-white shadow-sm border-b border-gray-200">
                <div className="container mx-auto px-4 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Admin Dashboard</h1>
                            <p className="text-sm text-gray-600 mt-1">Monitor platform performance and manage operations</p>
                        </div>
                        {/* Logout is available in the navbar; page-level logout removed */}
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white border-b border-gray-200">
                        <div className="container mx-auto px-4">
                            <div className="flex gap-4 md:gap-8 overflow-x-auto">
                        {ADMIN_TABS.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-4 px-2 border-b-2 font-medium transition-colors whitespace-nowrap text-sm md:text-base ${activeTab === tab.id
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-600 hover:text-gray-800'
                                    }`}
                            >
                                <span className="flex items-center gap-2">
                                    <span>{tab.icon}</span>
                                    {tab.label}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-6 md:py-8">
                {activeTab === 'dashboard' && (
                    <>
                        {/* Stats Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
                            <StatCard
                                title="Total Revenue"
                                value={`$${stats?.totalRevenue?.toLocaleString()}`}
                                subtitle={stats?.revenueTrend?.period}
                                trend={stats?.revenueTrend}
                                icon={
                                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                }
                                iconBg="bg-green-100"
                            />
                            <StatCard
                                title="Active Movers"
                                value={stats?.activeMovers}
                                subtitle={stats?.moversTrend?.period}
                                trend={stats?.moversTrend}
                                icon={
                                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                }
                                iconBg="bg-blue-100"
                            />
                            <StatCard
                                title="Total Leads"
                                value={stats?.totalLeads}
                                subtitle={stats?.leadsPeriod}
                                icon={
                                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                }
                                iconBg="bg-purple-100"
                            />

                            <StatCard
                                title="Pending Actions"
                                value={stats?.pendingActions}
                                subtitle={stats?.actionsNote}
                                icon={
                                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                }
                                iconBg="bg-orange-100"
                            />
                        </div>

                        {/* Charts Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Revenue & Leads Trend */}
                            <div className="bg-white rounded-xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-200">
                                <h3 className="text-lg font-bold text-gray-800 mb-4">Revenue & Leads Trend</h3>
                                <RevenueLeadsChart labels={labels} leads={leadsSeries} revenue={revenueSeries} />
                            </div>

                            {/* Lead Status Distribution */}
                            <div className="bg-white rounded-xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-200">
                                <h3 className="text-lg font-bold text-gray-800 mb-4">Lead Status Distribution</h3>
                                <LeadStatusPie labels={pieLabels} values={pieValues} />
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'users' && <UserManagement />}

                {activeTab === 'movers' && <MoverManagement />}

                {activeTab === 'trucker-applications' && <TruckerRegistrationApplications />}

                {activeTab === 'leads' && <LeadsManagement />}

                {activeTab === 'pending-documents' && <PendingDocuments />}

                {activeTab === 'instant-leads' && <InstantLeads />}

                {activeTab === 'reviews' && <MoverReviews />}

                {selectedApplication && (
                    <MoverApplicationModal
                        application={selectedApplication}
                        onClose={() => setSelectedApplication(null)}
                        onApprove={handleApprove}
                        onReject={handleReject}
                    />
                )}

                {/* Removed placeholder section to avoid showing 'under development' card */}
            </div>
        </div>
    );
};

export default Admin;
